/**
 * Status/tone → design-token lookups shared across the Cultural Intelligence components (legend,
 * map badges, trend-type pills, sparklines). Centralized so every component
 * reads the same palette instead of re-deriving it (Figma node 3249:11523).
 */

export const TREND_STATUS_META = {
  emerging: {
    label: 'Emerging',
    fg: 'var(--decoration-violet-regular-fg)',
    bg: 'var(--decoration-violet-regular-bg)',
  },
  seasonal: {
    label: 'Seasonal',
    fg: 'var(--decoration-lavender-regular-fg)',
    bg: 'var(--decoration-lavender-regular-bg)',
  },
  persistent: {
    label: 'Persistent',
    fg: 'var(--decoration-mint-regular-fg)',
    bg: 'var(--decoration-mint-regular-bg)',
  },
  peaking: {
    label: 'Peaking',
    fg: 'var(--decoration-linen-regular-fg)',
    bg: 'var(--decoration-linen-regular-bg)',
  },
};

/**
 * The filled "_trendtype" pill and the trend sparkline (Figma 3753:12258) — a separate palette from
 * TREND_STATUS_META: the new trend cards use the brand for Emerging and a purple for Persistent.
 * `line` names a CSS variable (not a `var()`), since Highcharts needs the resolved colour; `solid`
 * fills the numbered badges and legend dots of the "Where to focus first" map (Figma 3697:16234),
 * whose legend calls Seasonal "Seasonal (timing)".
 */
export const TREND_TYPE_PILL = {
  emerging: {
    fg: 'var(--brand-content)',
    bg: 'var(--brand-surface)',
    line: '--brand-graphic',
    solid: 'var(--brand-graphic)',
    legend: 'Emerging',
  },
  seasonal: {
    fg: 'var(--decoration-lavender-regular-fg)',
    bg: 'var(--ci-trend-seasonal-surface)',
    line: '--decoration-lavender-regular-fg',
    solid: 'var(--decoration-lavender-regular-fg)',
    legend: 'Seasonal (timing)',
  },
  persistent: {
    fg: 'var(--ci-trend-persistent)',
    bg: 'var(--ci-trend-persistent-surface)',
    line: '--ci-trend-persistent',
    solid: 'var(--ci-trend-persistent)',
    legend: 'Persistent',
  },
  peaking: {
    fg: 'var(--ci-trend-peaking)',
    bg: 'var(--ci-trend-peaking-surface)',
    line: '--ci-trend-peaking',
    solid: 'var(--ci-trend-peaking)',
    legend: 'Peaking',
  },
};

/**
 * Canonical culture name → emoji, the full taxonomy the Cultural Intelligence backend classifies
 * trends into (independent of which 5 a given brand's "Cultures you touch" happens to surface).
 * `CultureCard` looks emoji up from here by name rather than carrying its own `emoji` field, so every
 * caller renders a culture identically without having to know this mapping.
 */
export const CULTURE_EMOJI_MAP = {
  'Technology & Digital Culture': '💻',
  'Internet, Meme & Humor Culture': '😂',
  'Gaming Culture': '🎮',
  'Finance, Investing & Crypto Culture': '📈',
  'Business, Work & Creator-Economy Culture': '💼',
  'Personal Style, Beauty & Aesthetic Culture': '💄',
  'Consumer, Brand, Luxury & Shopping Culture': '🛍️',
  'Health, Fitness, Wellness & Self-Development Culture': '🏋️',
  'Food, Diet & Beverage Culture': '🍔',
  'Entertainment, Music, Screen & Fandom Culture': '🎬',
  'Sports Culture': '⚽',
  'Travel, Outdoor & Adventure Culture': '✈️',
  'Automotive & Personal-Mobility Culture': '🚗',
  'Home, Sustainable & Self-Reliant Living Culture': '🏡',
  'Creative, Maker, Collecting & Hobby Culture': '🎨',
  'Relationships, Family & Life-Event Culture': '💍',
  'Education, Youth, Learning & Research Culture': '🎓',
  'Pet & Animal Culture': '🐾',
  'News, Politics & Civic-Advocacy Culture': '📰',
  'Faith & Spirituality Culture': '🙏',
  'Place, Migration, Language & Local-Identity Culture': '🌍',
  'Generational & Nostalgia Culture': '📼',
};

/** Fallback for a culture name the map above doesn't (yet) recognize. */
export const DEFAULT_CULTURE_EMOJI = '✨';

/**
 * Acceleration text color for the shared Relevance/Momentum/Acceleration stat row
 * (Figma 3203:5279, `Stat` "ACCELERATION" value), keyed by the acceleration copy
 * the API returns.
 */
export const ACCELERATION_STATUS_META = {
  Increasing: { fg: 'var(--status-success-content)' },
  Steady: { fg: 'var(--content-subtle, #8a7a7b)' },
  Slowing: { fg: 'var(--status-warning-content)' },
};

const isScore = (value) => typeof value === 'number' && Number.isFinite(value);

/** An Opportunity score (0–100) as "92%", or "—" when the BE has none. */
export const formatPercent = (value) => (isScore(value) ? `${value}%` : '—');

/**
 * Momentum as a signed change: "+88%", "-12%", or "—" when the BE has none. The word after it
 * ("Surging", "Flat"…) waits for the ML's terminology.
 */
export const formatMomentum = (value) => {
  if (!isScore(value)) return '—';
  return `${value > 0 ? '+' : ''}${value}%`;
};
